/**
 * 工具站 SEO 内容单一数据源（SSOT）
 *
 * ⚠️ 两个消费方必须共用这份数据，内容不一致会变成 cloaking（对蜘蛛和对用户显示不同内容）：
 *   1) 前端：`components/common/ToolSeoSection.vue` 把它真实渲染在页面底部（用户可见）
 *   2) 构建期：`scripts/prerender.js` 把它写进 dist/<route>/index.html（蜘蛛可见）
 *
 * ⚠️ 本文件刻意写成 CommonJS：
 *   · Node 端（prerender.js）要能直接 require
 *   · webpack 端（Vue SFC）要能直接 import
 *   写成 ESM 会让 Node require 报 "Unexpected token 'export'"，写成 .mjs 又要改一堆别名。
 *   前端请用默认导入：import toolSeo from '@/data/tool-seo.js'
 *
 * 内容要求：全部为真实能力描述，与页面实际提供的工具一一对应，禁止夸大或编造。
 */

const SITE = 'https://tools.ceanx.com';
const BRAND = '纯净未知';
const COMPANY = '广州市辰翔科技有限公司';

/**
 * 每个路由的 SEO 内容。key 与 vue-router 的 path 一致。
 * 字段：
 *   path         路由（与 router/index.js 保持一致）
 *   title        <title>（<=30 个汉字，核心词前置）
 *   description  meta description
 *   keywords     meta keywords（百度已基本忽略，保留无害，别堆词）
 *   h1           静态内容主标题
 *   intro        简介段落（数组）
 *   list         能力清单 [{ name, desc }]
 *   steps        使用步骤（数组）
 *   faq          常见问题 [{ q, a }]
 */
const TOOL_SEO = {
  '/': {
    path: '/',
    title: '在线文档转换 - Excel/PDF/Word/HTML 互转 | 纯净未知',
    description:
      '免费在线文档格式互转：Excel、PDF、Word、HTML 多格式互相转换，高保真还原版式与样式。' +
      '网页版免安装免注册，上传文件与转换结果 7 天后自动删除。',
    keywords: '在线文档转换,Excel转PDF,PDF转Word,Word转PDF,HTML转PDF,格式互转',
    h1: '在线文件转换器',
    intro: [
      '纯净未知是广州市辰翔科技有限公司自研的在线文档处理工具，支持 Excel、PDF、Word、HTML 等主流办公格式互相转换，转换过程尽可能保留原有版式、字体与表格结构。',
      '网页版免安装、免注册，打开浏览器即可使用；上传的文件与转换结果会在 7 天后自动删除，不保留副本。',
    ],
    list: [
      { name: 'Excel 转 PDF', desc: '表格转 PDF，保留分页与表头，适合报表归档与对外发送。' },
      { name: 'PDF 转 Word', desc: '把 PDF 还原成可编辑的 Word 文档，便于二次修改。' },
      { name: 'Word 转 PDF', desc: '文档转 PDF 固化版式，避免在不同设备上排版错乱。' },
      { name: 'HTML 转 PDF', desc: '网页与 HTML 文件转 PDF，保留原有布局。' },
    ],
    steps: [
      '选择要上传的文件，可从本地直接选取。',
      '选择目标格式，系统会即时显示该转换的保真程度提示。',
      '点击转换，完成后直接下载结果文件。',
    ],
    faq: [
      { q: '在线文档转换需要付费吗？', a: '网页版完全免费，无需注册登录即可使用。' },
      { q: '转换后的文件会保留多久？', a: '上传文件与转换结果会在 7 天后自动删除，不会长期留存。' },
      { q: '文件大小有限制吗？', a: '网页版单个文件上限为 10MB，单次最多上传 5 个文件。' },
    ],
  },

  '/pdf-tools': {
    path: '/pdf-tools/',
    title: 'PDF工具箱 - 合并拆分压缩水印在线处理 | 纯净未知',
    description:
      '免费在线 PDF 工具箱：PDF 合并、拆分、页面提取、删除、重排、旋转、压缩、加水印、' +
      '加密解密、提取图片与文字，共 12 项功能，免安装免注册。',
    keywords: 'PDF合并,PDF拆分,PDF压缩,PDF加水印,PDF加密,PDF提取文字,PDF旋转',
    h1: 'PDF 工具箱',
    intro: [
      'PDF 工具箱提供 12 项常用 PDF 处理能力：合并、拆分、页面提取、删除页面、重排、旋转、压缩、添加水印、加密、解密、提取图片、提取文字。',
      '全部在浏览器里提交、在服务端完成后直接下载，无需安装任何软件。文件与结果 7 天后自动删除。',
    ],
    list: [
      { name: '合并', desc: '将多个 PDF 合并为一个文件，支持一次选择多个文件。' },
      { name: '拆分', desc: '按页数拆分为多个文件，支持每页拆分或按区间拆分。' },
      { name: '页面提取', desc: '提取指定页码，支持 1,3-5 这样的页码写法。' },
      { name: '删除页面', desc: '删除 PDF 中指定的页面。' },
      { name: '重排', desc: '按自定义顺序重新排列页面，如 3,1,2。' },
      { name: '旋转', desc: '按 90 度、180 度、270 度旋转指定页面或全部页面。' },
      { name: '压缩', desc: '按屏幕、电子书、打印、印刷四档压缩 PDF 体积。' },
      { name: '水印', desc: '添加文字水印，可设置水印内容与透明度。' },
      { name: '加密', desc: '为 PDF 设置打开密码。' },
      { name: '解密', desc: '去除 PDF 打开密码（需要原密码）。' },
      { name: '提取图片', desc: '把 PDF 内嵌的图片提取出来。' },
      { name: '提取文字', desc: '把 PDF 中的文本提取为 TXT 文件。' },
    ],
    steps: [
      '在对应功能卡片上选择要处理的 PDF 文件。',
      '按需要填写参数（页码、角度、压缩等级等）。',
      '点击执行，处理完成后直接下载结果。',
    ],
    faq: [
      { q: 'PDF 工具箱收费吗？', a: '网页版全部功能免费使用，无需注册。' },
      { q: 'PDF 压缩会损失清晰度吗？', a: '压缩按不同档位处理，压缩率越高画质损失越明显。按用途选择电子书档或打印档即可。' },
      { q: '忘记 PDF 密码还能解密吗？', a: '不能。解密功能需要提供原密码，本工具不具备破解密码的能力。' },
    ],
  },

  '/image-tools': {
    path: '/image-tools/',
    title: '图片工具箱 - 压缩到指定KB、改尺寸裁剪水印 | 纯净未知',
    description:
      '免费在线图片工具：把图片压缩到指定 KB、按宽高改尺寸、按坐标裁剪、添加文字水印、' +
      '多图合成 PDF。免安装免注册，文件 7 天后自动删除。',
    keywords: '图片压缩,照片压缩到200KB,图片改尺寸,图片裁剪,图片加水印,图片转PDF',
    h1: '图片工具箱',
    intro: [
      '图片工具箱提供 5 项常用图片处理能力：压缩到指定体积、改尺寸、裁剪、添加水印、多图转 PDF。',
      '常用于报名材料、证件照、上传附件等对图片体积有硬性要求的场景。文件与结果 7 天后自动删除。',
    ],
    list: [
      { name: '压缩到指定体积', desc: '按目标体积（KB）自动压缩，可同时指定质量参数。' },
      { name: '改尺寸', desc: '按宽高缩放，高度留空时按比例等比缩放。' },
      { name: '裁剪', desc: '按 X、Y 坐标与宽高裁剪指定区域。' },
      { name: '水印', desc: '添加文字水印，可选居中、平铺或右下角位置与透明度。' },
      { name: '多图转 PDF', desc: '把多张图片按顺序合成一个 PDF 文件。' },
    ],
    steps: [
      '选择要处理的图片（支持常见图片格式）。',
      '填写目标参数，例如目标体积 200KB、目标宽度等。',
      '点击执行，完成后下载处理后的图片或 PDF。',
    ],
    faq: [
      { q: '能把照片压缩到 200KB 以内吗？', a: '可以。使用压缩到指定体积，把目标体积填 200 即可；如仍未达标，可适当降低质量参数。' },
      { q: '压缩后图片会变模糊吗？', a: '压缩会带来一定画质损失，目标体积越小损失越明显。建议按实际需求的最低可接受画质设置。' },
      { q: '图片会被保存吗？', a: '不会长期保存。上传文件与处理结果会在 7 天后自动删除。' },
    ],
  },

  '/codec-tools': {
    path: '/codec-tools/',
    title: '在线编码转换 - Base64/Hex/URL/Unicode 互转 | 纯净未知',
    description:
      '免费在线编码工具：Base64、Hex、URL、Unicode、HTML 实体编解码，以及字符集转换。' +
      '支持实时转换与从文件载入，免安装免注册。',
    keywords: 'Base64编码,Base64解码,URL编码,Unicode转换,HTML实体编码,字符集转换',
    h1: '编码工具箱',
    intro: [
      '编码工具箱支持 Base64、Hex、URL、Unicode、HTML 实体五类编解码，以及字符集转换，输入即时出结果。',
      '支持从文件载入内容，适合处理较长的文本或配置文件片段。',
    ],
    list: [
      { name: 'Base64', desc: 'Base64 编码与解码。' },
      { name: 'Hex', desc: '十六进制字符串与文本的互相转换。' },
      { name: 'URL', desc: 'URL 编解码，处理中文与特殊字符。' },
      { name: 'Unicode', desc: 'Unicode 转义与还原。' },
      { name: 'HTML 实体', desc: 'HTML 实体编解码。' },
      { name: '字符集', desc: '在不同字符集之间转换文本编码。' },
    ],
    steps: [
      '在左侧输入框粘贴或载入内容。',
      '选择转换类型与编码或解码方向。',
      '右侧即时显示结果，可直接复制。',
    ],
    faq: [
      { q: '编码转换会上传到服务器吗？', a: '文本编解码在页面内实时完成，不产生文件留存。' },
      { q: '支持一次性处理很长的文本吗？', a: '支持，可通过载入文件读取较长内容。' },
    ],
  },

  '/ocr': {
    path: '/ocr/',
    title: 'OCR文字识别 - 扫描件图片转可编辑文字 | 纯净未知',
    description:
      '免费在线 OCR 文字识别：上传扫描件或拍照图片，自动识别为可编辑文本，' +
      '支持批量提交与结果下载。免安装免注册，文件 7 天后自动删除。',
    keywords: 'OCR文字识别,扫描件转文字,图片转文字,拍照识别文字,扫描件转Word',
    h1: 'OCR 识别',
    intro: [
      'OCR 识别把扫描件、拍照图片中的文字自动转成可编辑文本，适用于合同、发票、证件、书籍扫描页等场景。',
      '支持批量提交，识别结果可在任务中心查看与下载。上传文件与结果会在 7 天后自动删除。',
    ],
    list: [
      { name: '扫描件识别', desc: '识别扫描生成的文档图片，输出可编辑文本。' },
      { name: '拍照识别', desc: '识别手机拍照的文档、票据等图片中的文字。' },
      { name: '批量提交', desc: '一次提交多个文件，在任务中心统一查看进度。' },
      { name: '结果下载', desc: '识别完成后可直接下载文本结果。' },
    ],
    steps: [
      '上传需要识别的图片或 PDF 扫描件。',
      '提交识别任务，可在任务中心查看处理进度。',
      '识别完成后查看并下载文本结果。',
    ],
    faq: [
      { q: 'OCR 识别准确吗？', a: '清晰、正向、无遮挡的印刷体文档识别率最高；手写体、倾斜或低分辨率图片识别率会下降。' },
      { q: 'OCR 收费吗？', a: '网页版免费使用，无需注册登录。' },
      { q: '识别结果保存多久？', a: '上传文件与识别结果会在 7 天后自动删除。' },
    ],
  },
};

/** 需要生成静态 HTML、并允许收录的路由（/tasks 是个人任务列表，不收录） */
const SEO_ROUTES = Object.keys(TOOL_SEO);

/** 站点常量 */
const SEO_SITE = {
  url: SITE,
  brand: BRAND,
  company: COMPANY,
  /** sitemap 的 lastmod（内容真实修改日期，别每次发版都改成当天） */
  lastmod: '2026-10-10',
  /**
   * GEO 时效信号：published 是这批内容首次上线日，updated 是最近一次真实改动日。
   * ⛔ 不要填构建当天 —— 伪造新鲜度会被答案引擎的重复抓取对比识别。
   */
  published: '2026-10-10',
  updated: '2026-11-03',
  /** 运营主体工商信息（结构化数据用，真实可核验，勿改数字） */
  creditCode: '91440101MA9UWCAG22',
  phone: '18620429891',
  address: '广州市天河区新塘大街 3 号 7 栋 309-170 房',
  /** 公司简介（Organization.description，与官网保持一致口径） */
  intro:
    '广州市辰翔科技有限公司成立于 2020 年，位于广州市天河区，专注文档处理与人工智能应用软件开发，' +
    '自研文档格式互转引擎，围绕办公场景的格式壁垒问题提供高保真的转换、处理与识别能力。',
  /** 官网（同一运营主体的另一站点，用于 Organization.sameAs） */
  homepage: 'https://ceanx.com',
  /**
   * 百度统计站点 ID（tongji.baidu.com 新建站点后给的 hm.js? 后面那串 32 位字符）。
   * ⛔ 必须用户在百度后台取得，不能编造。留空时不加载任何统计脚本。
   * ⛔ 不要用 GA / GTM 等境外统计：国内加载不到，还会拖慢首屏。
   */
  tongjiId: '',
};

/** 按路由取 SEO 内容；未知路由返回 null（渲染空区块） */
function getToolSeo(path) {
  if (!path) return null;
  const p = path.length > 1 ? path.replace(/\/+$/, '') : path;
  return TOOL_SEO[p] || null;
}

module.exports = {
  TOOL_SEO,
  SEO_ROUTES,
  SEO_SITE,
  getToolSeo,
};
